import uniqBy from 'lodash/uniqBy';

const toDate = (value) => {
    if (!value) {
        return null;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const getProductLifecycleLabels = (product, now = new Date()) => {
    const status = product?.status;
    const publishedAt = toDate(product?.publishedAt ?? product?.published_at);
    const expiredAt = toDate(product?.expiredAt ?? product?.expired_at);

    if (status === 'draft') {
        return ['DRAFT'];
    }

    if (status !== 'published') {
        return [];
    }

    const labels = [];

    if (publishedAt && publishedAt > now) {
        labels.push('SCHEDULED');
    }

    if (expiredAt && expiredAt <= now) {
        labels.push('EXPIRED');
    }

    return labels;
};

const isActualProduct = (product, now = new Date()) => {
    if (product?.status !== 'published') {
        return false;
    }

    const publishedAtValue = product?.publishedAt ?? product?.published_at;
    const expiredAtValue = product?.expiredAt ?? product?.expired_at;

    if (publishedAtValue && !toDate(publishedAtValue)) {
        return false;
    }

    if (expiredAtValue && !toDate(expiredAtValue)) {
        return false;
    }

    return getProductLifecycleLabels(product, now).length === 0;
};

function getStatesFromProducts(products) {
    let allStates = [];
    if (Array.isArray(products) && products.length > 0) {
        allStates = products.filter((product) => isActualProduct(product)).map((product) => {
            // only available product platform states
            return product.states || [];
        }).reduce((prev, curr) => {
            return prev.concat(curr);
        }, []);
    }

    return uniqBy(allStates, 'id');
}

const collectStates = (states, products) => {
    const activeProducts = products ? products.filter((product) => isActualProduct(product)) : [];

    if (states && states.length > 0) {
        if (products && products.length > 0) {
            const activeProductStateIds = new Set(
                activeProducts.flatMap((p) => (p.states || []).map((s) => s.id)),
            );
            return states.filter((state) => activeProductStateIds.has(state.id));
        }
        return states;
    }

    return getStatesFromProducts(activeProducts);
};

const getProductPageVisibilityLabels = ({
    status,
    pageStatus,
    publishedAt: publishedAtParam,
    expiredAt: expiredAtParam,
    isAdmin = false,
    now = new Date(),
} = {}) => {
    if (!isAdmin) {
        return [];
    }

    const publishedAt = toDate(publishedAtParam);
    const expiredAt = toDate(expiredAtParam);

    const isDraft = status === 'draft' || pageStatus === 'draft';
    const isExpired = Boolean(expiredAt && expiredAt <= now);
    const isScheduled = Boolean(publishedAt && publishedAt > now);

    if (isDraft || isExpired) {
        return [
            { text: 'HIDDEN', className: 'text-danger' },
            { text: isDraft ? 'DRAFT' : 'EXPIRED', className: 'text-danger' },
        ];
    }

    if (isScheduled) {
        return [
            { text: 'SCHEDULED', className: 'text-warning' },
        ];
    }

    return [];
};

export {
    getStatesFromProducts,
    collectStates,
    isActualProduct,
    getProductLifecycleLabels,
    getProductPageVisibilityLabels,
};
